/**
 * A tela do convite, inteira, sem saber o que é rede.
 *
 * Ela recebe `EstadoDoConvite` e devolve pixels. Nenhuma chamada, nenhum
 * efeito, nenhum `await` — e é por isso que cada estado (carregando, fora da
 * janela, rede caída no meio do preenchimento, fim com instruções) pode ser
 * renderizado em teste e em captura de tela sem um Supabase de pé.
 *
 * A ordem dos blocos é a mesma nas três etapas: quem convida, o que se pede
 * agora, o que já foi respondido.
 */

import type { EstadoDoConvite } from '../../ganchos/useConvite';
import type { Valor } from '../../tipos';
import { Esqueleto } from './Esqueleto';
import { Fim } from './Fim';
import { Mensagem } from './Mensagem';
import { Cabecalho, Moldura } from './Moldura';
import { Pergunta } from './Pergunta';
import { Falha, Recusa } from './Recusa';
import { Trilha } from './Trilha';
import { Video } from './Video';

import '../../estilo/convite.css';

export type PropsDaTela = {
  readonly estado: EstadoDoConvite;
  readonly aoEnviar: (valor: Valor) => void;
  readonly aoReabrir: () => void;
  /**
   * "Não sou eu", na tela final de uma resposta retomada. Opcional porque a
   * prévia do construtor monta esta tela sem gancho nenhum — e lá não há
   * navegador com resposta lembrada para descartar.
   */
  readonly aoNaoSerVoce?: () => void;
};

export function TelaDoConvite({ estado, aoEnviar, aoReabrir, aoNaoSerVoce }: PropsDaTela) {
  if (estado.situacao === 'carregando') {
    return (
      <Moldura marca={null}>
        <Esqueleto />
      </Moldura>
    );
  }

  if (estado.situacao === 'recusado') {
    // Recusa na porta não traz marca: `recusa_ao_convidado` devolve motivo e
    // mensagem, e mais nada. Sem tema do organizador, a moldura fica neutra —
    // é a única tela do convite em que a identidade não é dele.
    return (
      <Moldura marca={null}>
        <Recusa recusa={estado.recusa} aoRecomecar={aoReabrir} />
      </Moldura>
    );
  }

  if (estado.situacao === 'falhou') {
    return (
      <Moldura marca={null}>
        <Falha mensagem={estado.mensagem} aoTentarDeNovo={aoReabrir} />
      </Moldura>
    );
  }

  if (estado.situacao === 'concluido') {
    return (
      <Moldura marca={estado.cabecalho.marca}>
        <Cabecalho
          titulo={estado.cabecalho.titulo}
          descricao={estado.cabecalho.descricao}
          marca={estado.cabecalho.marca}
          compacto={false}
        />
        {/* O vídeo aparece na tela final SÓ quando ela é a abertura — trilha
            vazia, ninguém respondeu nada.
            
            Parece caso de borda e não é: um formulário "assista ao vídeo e
            clique para acessar a plataforma" é início → fim, sem pergunta
            nenhuma. Ele cai direto aqui, e sem esta linha o vídeo — que é a
            razão de o formulário existir — nunca apareceria.
            
            Depois de responder algo, não aparece: aí a tela deixou de ser
            "quem convida" e virou "o que aconteceu", e um iframe pesando em
            cima da despedida não serve a ninguém. É a mesma regra da tela de
            perguntas, medida pela mesma trilha. */}
        {estado.trilha.length === 0 && estado.cabecalho.marca.video_url ? (
          <Video url={estado.cabecalho.marca.video_url} titulo={estado.cabecalho.titulo} />
        ) : null}
        <Fim
          no={estado.no}
          mensagem={estado.mensagem}
          instrucoes={estado.instrucoes}
          trilha={estado.trilha}
          retomada={estado.retomada}
          emailMascarado={estado.emailMascarado}
          aoNaoSerVoce={aoNaoSerVoce}
        />
      </Moldura>
    );
  }

  const { no, trilha, enviando, problema } = estado;
  const jaRespondeu = trilha.length > 0;

  return (
    <Moldura marca={estado.cabecalho.marca}>
      <Cabecalho
        titulo={estado.cabecalho.titulo}
        descricao={estado.cabecalho.descricao}
        marca={estado.cabecalho.marca}
        compacto={jaRespondeu}
      />

      {/* O vídeo é a abertura, e só ela: `jaRespondeu` é a MESMA trilha que
          encolhe o cabeçalho, e pela mesma razão — a partir da primeira
          resposta a tela deixa de ser "quem convida" para virar "o que
          falta". Um iframe de vídeo sobrevivendo pergunta afora pesaria o
          dobro do que um cabeçalho compacto já corrige. */}
      {!jaRespondeu && estado.cabecalho.marca.video_url ? (
        <Video url={estado.cabecalho.marca.video_url} titulo={estado.cabecalho.titulo} />
      ) : null}

      <div className="convite-corpo">
        {/* `key` é o que faz o rascunho morrer com a pergunta e sobreviver a
            uma falha de envio: nó novo, componente novo. */}
        {no.tipo === 'pergunta' ? (
          <Pergunta
            key={no.id}
            no={no}
            enviando={enviando}
            problema={problema}
            focar={jaRespondeu}
            aoEnviar={aoEnviar}
          />
        ) : (
          <Mensagem
            key={no.id}
            no={no}
            enviando={enviando}
            problema={problema}
            focar={jaRespondeu}
            aoSeguir={() => aoEnviar(null)}
          />
        )}

        <Trilha itens={trilha} />
      </div>
    </Moldura>
  );
}
